import { lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { SuspensedView } from "../components/layout/SuspensedView";
import { MainLayout } from "../components/layout/MainLayout";
import RequiredRole from "../components/auth/RequiredRole";
import ActivityWrapper from "../pages/ActivityLog/ActivityWrapper";
import ToVerifyAnalyseManagement from "../pages/AnalyseManagement/Keywords/ToVerifyAnalyseManagement/ToVerifyAnalyseManagement";
import VerifiedKeywordAnalyseManagement from "../pages/AnalyseManagement/Keywords/VerifiedKeywordAnalyseManagement/VerifiedKeywordAnalyseManagement";
import SiteAnalyseManagementWrapper from "../pages/AnalyseManagement/Site/SiteAnalyseManagementWrapper";
import UrlsAnalyzedManagementWrapper from "../pages/AnalyseManagement/Urls/UrlsAnalyzedManagementWrapper";
import ApproveGrid from "../pages/ContentManagement/ApproveGrid/ApproveGrid";
import DeliveredGrid from "../pages/ContentManagement/DeliveredGrid/DeliveredGrid";
import WaitingGrid from "../pages/ContentManagement/WaitingGrid/WaitingGrid";
import DashboardWrapper from "../pages/Dashboard/DashboardWrapper";
import LibraryGrid from "../pages/LibraryManagement/LibraryGrid/LibraryGrid";
import { UserRole } from "../transports/user";
import Imageia from "../pages/ImageIA/Imageia";
import PublishManagementWrapper from "../pages/ContentManagement/PublishGrid/PublishManagementWrapper";
import OnlineManagementWrapper from "../pages/ContentManagement/OnlineGrid/OnlineManagementWrapper";
import TitleIAGrid from "../pages/TitleIa/TitleIAGrid";
import ReviewedManagerWrapper from "../pages/ContentManagement/ReviewedGrid/ReviewedManagerWrapper";
import WriterManagementWrapper from "../pages/ContentManagement/WriteGrid/WriterManagementWrapper";
import SitesAnalyzeManagementWrapper from "../pages/AnalyseManagement/Sites/SitesAnalyzeManagementWrapper";
import StockGrid from "../pages/ContentManagement/StockGrid/StockGrid";
import ProcessGrid from "../pages/ContentManagement/ProcessGrid/ProcessGrid";
import ProofreadGrid from "../pages/ContentManagement/ProofreadGrid/ProofreadGrid";

const PrivateRoutes = () => {
    const ProfilePage = lazy(() => import("../modules/profile/ProfilePage"));
    const SiteManagementWrapper = lazy(
        () => import("../pages/SiteManagement/SiteManagementWrapper")
    );
    const DirectoryManagementWrapper = lazy(
        () => import("../pages/DirectoryManagemet/DirectoryManagementWrapper")
    );
    const PlatformManagementWrapper = lazy(
        () => import("../pages/PlatformManagement/PlatformManagementWrapper")
    );
    const SpaceManagement = lazy(() => import("../pages/SpaceManagement/SpaceManagementWrapper"));

    const StatsManagementWrapper = lazy(
        () => import("../pages/StatsManagement/StatsManagementWrapper")
    );
    const BudgetsManagementWrapper = lazy(
        () => import("../pages/BudgetsManagement/BudgetsManagementWrapper")
    );
    const ReportWriter = lazy(() => import("../pages/ReportManagement/ReportWriter/ReportWriter"));

    const ReportProofreader = lazy(
        () => import("../pages/ReportManagement/ReportProofreader/ReportProofreader")
    );
    const MessageCenterManagerWrapper = lazy(
        () => import("../pages/MessageCenter/MessageCenterManagerWrapper")
    );
    const BoostGrid = lazy(() => import("../pages/AnalyseManagement/BoostGrid/BoostGrid"));

    return (
        <Routes>
            <Route element={<MainLayout />}>
                {/* Redirect to Dashboard after success login/registartion */}
                <Route path="auth/*" element={<Navigate to="/dashboard" />} />
                {/* Pages */}
                <Route path="dashboard" element={<DashboardWrapper />} />
                <Route
                    path="library"
                    element={
                        <SuspensedView>
                            <LibraryGrid />
                        </SuspensedView>
                    }
                />
                <Route path="activity" element={<ActivityWrapper />} />
                <Route path="imageia" element={<Imageia />} />
                <Route path="titleia" element={<TitleIAGrid />} />
                <Route
                    path="user/profile"
                    element={
                        <SuspensedView>
                            <ProfilePage />
                        </SuspensedView>
                    }
                />

                <Route path="*" element={<Navigate to="/error/404" />} />

                {/* Admin Only Page */}
                <Route element={<RequiredRole allowedRoles={[UserRole.ROLE_ADMIN]} />}>
                    <Route
                        path="space-management/*"
                        element={
                            <SuspensedView>
                                <SpaceManagement />
                            </SuspensedView>
                        }
                    />

                    <Route
                        path="stats/*"
                        element={
                            <SuspensedView>
                                <StatsManagementWrapper />
                            </SuspensedView>
                        }
                    />
                    <Route
                        path="budgets/*"
                        element={
                            <SuspensedView>
                                <BudgetsManagementWrapper />
                            </SuspensedView>
                        }
                    />
                </Route>
                <Route
                    element={
                        <RequiredRole
                            allowedRoles={[
                                UserRole.ROLE_ADMIN,
                                UserRole.ROLE_LEAD_BUSINESS_MANAGER,
                                UserRole.ROLE_BUSINESS_MANAGER,
                            ]}
                        />
                    }
                >
                    <Route
                        path="message-center/*"
                        element={
                            <SuspensedView>
                                <MessageCenterManagerWrapper />
                            </SuspensedView>
                        }
                    />
                </Route>

                {/* Site AREA  */}
                <Route
                    element={
                        <RequiredRole
                            allowedRoles={[
                                UserRole.ROLE_ADMIN,
                                UserRole.ROLE_PROJECT_MANAGER,
                                UserRole.ROLE_LEAD_PROJECT_MANAGER,
                                UserRole.ROLE_LEAD_BUSINESS_MANAGER,
                                UserRole.ROLE_LEAD_WEBMASTER,
                                UserRole.ROLE_WEBMASTER,
                            ]}
                        />
                    }
                >
                    <Route
                        path="sites/*"
                        element={
                            <SuspensedView>
                                <SiteManagementWrapper />
                            </SuspensedView>
                        }
                    />
                </Route>

                {/* Directory AREA */}
                <Route
                    element={
                        <RequiredRole
                            allowedRoles={[
                                UserRole.ROLE_ADMIN,
                                UserRole.ROLE_PROJECT_MANAGER,
                                UserRole.ROLE_LEAD_PROJECT_MANAGER,
                                UserRole.ROLE_LEAD_LINKER,
                                UserRole.ROLE_LINKER,
                            ]}
                        />
                    }
                >
                    <Route
                        path="directory/*"
                        element={
                            <SuspensedView>
                                <DirectoryManagementWrapper />
                            </SuspensedView>
                        }
                    />
                </Route>

                {/* PLATFORMS AREA */}
                <Route
                    element={
                        <RequiredRole
                            allowedRoles={[
                                UserRole.ROLE_ADMIN,
                                UserRole.ROLE_BUSINESS_MANAGER,
                                UserRole.ROLE_LEAD_BUSINESS_MANAGER,
                            ]}
                        />
                    }
                >
                    <Route
                        path="platforms/*"
                        element={
                            <SuspensedView>
                                <PlatformManagementWrapper />
                            </SuspensedView>
                        }
                    />
                </Route>

                {/* CONTENT AREA */}

                <Route
                    element={
                        <RequiredRole
                            allowedRoles={[
                                UserRole.ROLE_ADMIN,
                                UserRole.ROLE_LEAD_PROJECT_MANAGER,
                                UserRole.ROLE_PROJECT_MANAGER,
                                UserRole.ROLE_BUSINESS_MANAGER,
                                UserRole.ROLE_LEAD_BUSINESS_MANAGER,
                                UserRole.ROLE_LEAD_WRITER,
                                UserRole.ROLE_LEAD_WORKER,
                                UserRole.ROLE_WORKER,
                            ]}
                        />
                    }
                >
                    <Route
                        path="content/stock/*"
                        element={
                            <SuspensedView>
                                <StockGrid />
                            </SuspensedView>
                        }
                    />
                </Route>

                <Route
                    element={
                        <RequiredRole
                            allowedRoles={[
                                UserRole.ROLE_ADMIN,
                                UserRole.ROLE_LEAD_PROJECT_MANAGER,
                                UserRole.ROLE_PROJECT_MANAGER,
                                UserRole.ROLE_LEAD_BUSINESS_MANAGER,
                                UserRole.ROLE_BUSINESS_MANAGER,
                                UserRole.ROLE_LINKER,
                                UserRole.ROLE_LEAD_LINKER,
                                UserRole.ROLE_LEAD_WORKER,
                                UserRole.ROLE_WORKER,
                            ]}
                        />
                    }
                >
                    <Route
                        path="content/toPublish/*"
                        element={
                            <SuspensedView>
                                <PublishManagementWrapper />
                            </SuspensedView>
                        }
                    />
                </Route>

                <Route
                    element={
                        <RequiredRole
                            allowedRoles={[
                                UserRole.ROLE_ADMIN,
                                UserRole.ROLE_LEAD_PROJECT_MANAGER,
                                UserRole.ROLE_PROJECT_MANAGER,
                                UserRole.ROLE_LINKER,
                                UserRole.ROLE_LEAD_LINKER,
                            ]}
                        />
                    }
                >
                    <Route
                        path="content/toProcess/*"
                        element={
                            <SuspensedView>
                                <ProcessGrid />
                            </SuspensedView>
                        }
                    />
                </Route>

                <Route
                    element={
                        <RequiredRole
                            allowedRoles={[
                                UserRole.ROLE_ADMIN,
                                UserRole.ROLE_LEAD_PROJECT_MANAGER,
                                UserRole.ROLE_PROJECT_MANAGER,
                                UserRole.ROLE_LEAD_BUSINESS_MANAGER,
                                UserRole.ROLE_BUSINESS_MANAGER,
                                UserRole.ROLE_WRITER,
                                UserRole.ROLE_LEAD_WRITER,
                                UserRole.ROLE_LEAD_WORKER,
                                UserRole.ROLE_WORKER,
                                UserRole.ROLE_LEAD_LINKER,
                                UserRole.ROLE_LINKER,
                            ]}
                        />
                    }
                >
                    <Route
                        path="content/toWrite/*"
                        element={
                            <SuspensedView>
                                <WriterManagementWrapper />
                            </SuspensedView>
                        }
                    />
                </Route>

                <Route
                    element={
                        <RequiredRole
                            allowedRoles={[
                                UserRole.ROLE_LEAD_WRITER,
                                UserRole.ROLE_WRITER,
                                UserRole.ROLE_ADMIN,
                                UserRole.ROLE_LEAD_LINKER,
                                UserRole.ROLE_LINKER,
                            ]}
                        />
                    }
                >
                    <Route
                        path="content/delivered"
                        element={
                            <SuspensedView>
                                <DeliveredGrid />
                            </SuspensedView>
                        }
                    />
                </Route>

                <Route
                    element={
                        <RequiredRole
                            allowedRoles={[
                                UserRole.ROLE_ADMIN,
                                UserRole.ROLE_LEAD_PROJECT_MANAGER,
                                UserRole.ROLE_PROJECT_MANAGER,
                                UserRole.ROLE_LEAD_BUSINESS_MANAGER,
                                UserRole.ROLE_BUSINESS_MANAGER,
                                UserRole.ROLE_PROOFREADER,
                                UserRole.ROLE_LEAD_PROOFREADER,
                                UserRole.ROLE_LEAD_WORKER,
                                UserRole.ROLE_WORKER,
                                UserRole.ROLE_LEAD_LINKER,
                                UserRole.ROLE_LINKER,
                            ]}
                        />
                    }
                >
                    <Route
                        path="content/toProofread/*"
                        element={
                            <SuspensedView>
                                <ProofreadGrid />
                            </SuspensedView>
                        }
                    />
                </Route>
                <Route
                    element={
                        <RequiredRole
                            allowedRoles={[
                                UserRole.ROLE_LEAD_PROOFREADER,
                                UserRole.ROLE_PROOFREADER,
                                UserRole.ROLE_ADMIN,
                            ]}
                        />
                    }
                >
                    <Route
                        path="content/reviewed/*"
                        element={
                            <SuspensedView>
                                <ReviewedManagerWrapper />
                            </SuspensedView>
                        }
                    />
                </Route>

                <Route
                    element={
                        <RequiredRole
                            allowedRoles={[
                                UserRole.ROLE_ADMIN,
                                UserRole.ROLE_LEAD_PROJECT_MANAGER,
                                UserRole.ROLE_PROJECT_MANAGER,
                                UserRole.ROLE_LEAD_BUSINESS_MANAGER,
                                UserRole.ROLE_BUSINESS_MANAGER,
                                UserRole.ROLE_LINKER,
                                UserRole.ROLE_LEAD_PROOFREADER,
                                UserRole.ROLE_LEAD_WRITER,
                                UserRole.ROLE_LEAD_LINKER,
                            ]}
                        />
                    }
                >
                    <Route
                        path="content/online/*"
                        element={
                            <SuspensedView>
                                <OnlineManagementWrapper />
                            </SuspensedView>
                        }
                    />
                </Route>

                <Route
                    element={
                        <RequiredRole
                            allowedRoles={[
                                UserRole.ROLE_ADMIN,
                                UserRole.ROLE_LEAD_PROJECT_MANAGER,
                                UserRole.ROLE_PROJECT_MANAGER,
                                UserRole.ROLE_LINKER,
                                UserRole.ROLE_LEAD_LINKER,
                            ]}
                        />
                    }
                >
                    <Route
                        path="content/waiting"
                        element={
                            <SuspensedView>
                                <WaitingGrid />
                            </SuspensedView>
                        }
                    />
                </Route>

                <Route
                    element={
                        <RequiredRole
                            allowedRoles={[
                                UserRole.ROLE_ADMIN,
                                UserRole.ROLE_LEAD_PROJECT_MANAGER,
                                UserRole.ROLE_PROJECT_MANAGER,
                                UserRole.ROLE_LEAD_PROOFREADER,
                                UserRole.ROLE_LEAD_BUSINESS_MANAGER,
                                UserRole.ROLE_BUSINESS_MANAGER,
                                UserRole.ROLE_LINKER,
                                UserRole.ROLE_LEAD_LINKER,
                            ]}
                        />
                    }
                >
                    <Route
                        path="content/toApprove"
                        element={
                            <SuspensedView>
                                <ApproveGrid />
                            </SuspensedView>
                        }
                    />
                </Route>

                {/* ANALYSE AREA */}
                <Route
                    element={
                        <RequiredRole
                            allowedRoles={[
                                UserRole.ROLE_ADMIN,
                                UserRole.ROLE_PROJECT_MANAGER,
                                UserRole.ROLE_LEAD_PROJECT_MANAGER,
                                UserRole.ROLE_LEAD_WORKER,
                                UserRole.ROLE_WORKER,
                            ]}
                        />
                    }
                >
                    <Route
                        path="analyse/keywords/verified/*"
                        element={
                            <SuspensedView>
                                <VerifiedKeywordAnalyseManagement />
                            </SuspensedView>
                        }
                    />

                    <Route
                        path="analyse/keywords/toVerify/*"
                        element={
                            <SuspensedView>
                                <ToVerifyAnalyseManagement />
                            </SuspensedView>
                        }
                    />

                    <Route
                        path="analyse/global/*"
                        element={
                            <SuspensedView>
                                <SitesAnalyzeManagementWrapper />
                            </SuspensedView>
                        }
                    />

                    <Route
                        path="analyse/zoom/*"
                        element={
                            <SuspensedView>
                                <SiteAnalyseManagementWrapper />
                            </SuspensedView>
                        }
                    />
                </Route>
                <Route
                    element={
                        <RequiredRole
                            allowedRoles={[
                                UserRole.ROLE_ADMIN,
                                UserRole.ROLE_LEAD_PROJECT_MANAGER,
                                UserRole.ROLE_PROJECT_MANAGER,
                            ]}
                        />
                    }
                >
                    <Route
                        path="analyse/urls/*"
                        element={
                            <SuspensedView>
                                <UrlsAnalyzedManagementWrapper />
                            </SuspensedView>
                        }
                    />
                </Route>
                <Route
                    element={
                        <RequiredRole
                            allowedRoles={[
                                UserRole.ROLE_ADMIN,
                                UserRole.ROLE_PROJECT_MANAGER,
                                UserRole.ROLE_LEAD_PROJECT_MANAGER,
                            ]}
                        />
                    }
                >
                    <Route
                        path="analyse/boost/*"
                        element={
                            <SuspensedView>
                                <BoostGrid />
                            </SuspensedView>
                        }
                    />
                </Route>
                {/* REPORTS AREA */}
                <Route
                    element={
                        <RequiredRole
                            allowedRoles={[
                                UserRole.ROLE_ADMIN,
                                UserRole.ROLE_LEAD_BUSINESS_MANAGER,
                                UserRole.ROLE_LEAD_LINKER,
                                UserRole.ROLE_LEAD_WRITER,
                                UserRole.ROLE_LEAD_PROOFREADER,
                                UserRole.ROLE_LEAD_PROJECT_MANAGER,
                            ]}
                        />
                    }
                >
                    <Route
                        path="reports/writers"
                        element={
                            <SuspensedView>
                                <ReportWriter />
                            </SuspensedView>
                        }
                    />
                    <Route
                        path="reports/proofreaders"
                        element={
                            <SuspensedView>
                                <ReportProofreader />
                            </SuspensedView>
                        }
                    />
                </Route>
            </Route>
        </Routes>
    );
};
export { PrivateRoutes };
